<script setup>
// One conversation in the condensed list: the Polaris kit's queue row (QueueItem, a composition of the registry
// Item) with the woot's data. The root stays a `div.conversation` (`.active` when open, `.selected` when picked for
// a bulk action): the list's keyboard shortcuts and the resolve action find rows by those classes.
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { evaluateSLAStatus } from 'dashboard/helper/slaHelper';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'next/polaris/ui/item';
import { Badge } from 'next/polaris/ui/badge';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import MessagePreview from './MessagePreview.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  // The contact panel's list (no avatar, narrower column): a tighter side padding.
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const VISIBLE_LABELS = 2;
const SLA_REFRESH_INTERVAL = 60000;

const { t } = useI18n();
const accountLabels = useMapGetter('labels/getLabels');

const hovered = ref(false);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const unreadDisplay = computed(() =>
  unreadCount.value > 9
    ? t('CHAT_LIST.UNREAD_COUNT_OVERFLOW')
    : unreadCount.value
);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const showAssigneeName = computed(
  () => props.showAssignee && !!props.assignee.name
);
const showMetaSection = computed(
  () => props.showInboxName || showAssigneeName.value
);
const isAgentBotAssignee = computed(
  () => props.chat?.meta?.assignee_type === 'AgentBot'
);

// The labels of the conversation, in the account's order (a label the account no longer has is not shown).
const conversationLabels = computed(() => {
  const titles = props.chat.labels || [];
  return (accountLabels.value || []).filter(({ title }) =>
    titles.includes(title)
  );
});
const visibleLabels = computed(() =>
  conversationLabels.value.slice(0, VISIBLE_LABELS)
);
const hiddenLabelsCount = computed(
  () => conversationLabels.value.length - visibleLabels.value.length
);

// SLA: the time left (or overdue) to answer, refreshed every minute while the row is on screen.
const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);
const slaStatus = ref({ threshold: '', isSlaMissed: false, type: '' });
const slaTimer = ref(null);
const updateSlaStatus = () => {
  slaStatus.value = hasSlaPolicyId.value
    ? evaluateSLAStatus({
        appliedSla: props.chat.applied_sla,
        chat: props.chat,
        slaEvents: props.chat.sla_events || [],
      })
    : { threshold: '', isSlaMissed: false, type: '' };
};
const scheduleSlaRefresh = () => {
  slaTimer.value = setTimeout(() => {
    updateSlaStatus();
    scheduleSlaRefresh();
  }, SLA_REFRESH_INTERVAL);
};
const showSla = computed(
  () => hasSlaPolicyId.value && !!slaStatus.value.threshold
);
const slaTitle = computed(() => {
  const type = slaStatus.value.type?.toUpperCase();
  if (!type) return '';
  return t(`CONVERSATION.HEADER.SLA_STATUS.${type}`, {
    status: t(
      `CONVERSATION.HEADER.SLA_STATUS.${slaStatus.value.isSlaMissed ? 'MISSED' : 'DUE'}`
    ),
  });
});

const showBadgesSection = computed(
  () => showSla.value || conversationLabels.value.length > 0
);

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

// The virtual list reuses a row for another conversation: reset what belongs to the previous one.
watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
watch(() => props.chat, updateSlaStatus);
watch(hasSlaPolicyId, updateSlaStatus);

onMounted(() => {
  updateSlaStatus();
  scheduleSlaRefresh();
});

onUnmounted(() => {
  if (slaTimer.value) clearTimeout(slaTimer.value);
});
</script>

<template>
  <Item
    size="sm"
    :aria-current="isActiveChat ? 'true' : undefined"
    class="conversation w-full cursor-pointer flex-nowrap items-start text-start text-polaris-foreground hover:bg-polaris-accent aria-[current=true]:bg-polaris-selected aria-[current=true]:text-polaris-selected-foreground"
    :class="{
      active: isActiveChat,
      'selected bg-polaris-muted/50': selected,
      'px-2': compact,
      'px-3': !compact,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <ItemMedia
      v-if="!hideThumbnail"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="absolute inset-0 z-10 flex cursor-pointer items-center justify-center rounded-full backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
    </ItemMedia>
    <ItemContent class="min-w-0 gap-0.5">
      <ItemTitle
        class="w-full justify-between"
        :class="isActiveChat || hasUnread ? 'font-medium' : 'font-normal'"
      >
        <span class="truncate capitalize">{{ currentContact.name }}</span>
        <span
          class="flex shrink-0 items-center gap-1.5 text-xs font-normal text-polaris-muted-foreground"
        >
          <CardPriorityIcon
            v-if="chat.priority"
            :priority="chat.priority"
            class="!size-3.5 shrink-0"
          />
          <span
            v-if="hasUnread"
            class="min-w-5 rounded-full bg-polaris-foreground px-1.5 text-center type-polaris-small-label text-polaris-background"
          >
            {{ unreadDisplay }}
            <span class="sr-only">{{ $t('CONVERSATION.CARD.UNREAD') }}</span>
          </span>
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
            last-activity-only
            class="!text-xs !leading-4 !text-polaris-muted-foreground"
          />
        </span>
      </ItemTitle>
      <!-- One line, cut with an ellipsis where the row ends (the registry's clamp cuts at a word and wastes the row). -->
      <ItemDescription
        class="mb-0 line-clamp-none text-xs"
        :class="{
          'text-polaris-selected-foreground': isActiveChat,
          'text-polaris-foreground': hasUnread && !isActiveChat,
        }"
      >
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          message-preview-class="!m-0 !h-auto !text-xs !leading-normal"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="block truncate"
        />
        <span v-else key="no-messages" class="block truncate">
          {{ $t('CHAT_LIST.NO_MESSAGES') }}
        </span>
      </ItemDescription>
      <p
        v-if="showMetaSection"
        class="mb-0 flex min-w-0 items-center gap-1.5 text-xs text-polaris-muted-foreground"
      >
        <ChannelIcon
          v-if="showInboxName && inbox.id"
          :inbox="inbox"
          class="size-3.5 shrink-0"
        />
        <span v-if="showInboxName" class="truncate" :title="inbox.name">
          {{ inbox.name }}
        </span>
        <span
          v-if="showInboxName && showAssigneeName"
          class="size-0.5 shrink-0 rounded-full bg-current"
          aria-hidden="true"
        />
        <span
          v-if="showAssigneeName"
          class="flex min-w-0 items-center gap-1"
          :title="assignee.name"
        >
          <Icon
            :icon="isAgentBotAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'"
            class="size-3 shrink-0"
          />
          <span class="truncate">{{ assignee.name }}</span>
        </span>
      </p>
      <div
        v-if="showBadgesSection"
        class="flex flex-wrap items-center gap-1 pt-1"
      >
        <Badge
          v-if="showSla"
          :variant="slaStatus.isSlaMissed ? 'destructive' : 'outline'"
          :title="slaTitle"
          data-queue-sla
        >
          <Icon icon="i-lucide-clock" />
          {{ slaStatus.threshold }}
        </Badge>
        <Label
          v-for="label in visibleLabels"
          :key="label.id"
          :label="label"
          compact
          class="max-w-full [&>span:last-of-type]:truncate"
          data-queue-label
        />
        <span
          v-if="hiddenLabelsCount > 0"
          class="type-polaris-small-label text-polaris-muted-foreground"
        >
          {{ `+${hiddenLabelsCount}` }}
          <span class="sr-only">{{ $t('CONVERSATION.CARD.MORE_LABELS') }}</span>
        </span>
      </div>
    </ItemContent>
  </Item>
</template>
